import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { entities, media } from "../services/api";
import { useApp } from "../context/AppContext";
import { extractProductData, parseReceipt } from "../utils/receiptParser";
import {
  CloseIcon,
  ProductIcon,
  CheckIcon,
  DocumentIcon,
  LinkIcon,
} from "./Icons";

function QuickAddModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { refreshCounts, refreshProducts } = useApp();
  const [receiptText, setReceiptText] = useState("");
  const [receiptHtml, setReceiptHtml] = useState("");
  const [parsedProducts, setParsedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [step, setStep] = useState("paste"); // paste | review | saving | done
  const [, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [failedProducts, setFailedProducts] = useState([]);
  const editorRef = useRef(null);

  useEffect(() => {
    if (isOpen && editorRef.current) {
      editorRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setReceiptText("");
      setReceiptHtml("");
      setParsedProducts([]);
      setSelectedProducts(new Set());
      setStep("paste");
      setSaving(false);
      setSavedCount(0);
      setFailedProducts([]);
      // Clear the editor content
      if (editorRef.current) {
        editorRef.current.innerHTML = "";
      }
    }
  }, [isOpen]);

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    if (clipboardData) {
      // Get HTML content if available (preserves links)
      const html = clipboardData.getData("text/html");
      const text = clipboardData.getData("text/plain");

      if (html) {
        setReceiptHtml(html);
        // Insert the HTML content into the contenteditable div
        editorRef.current.innerHTML = html;
      } else {
        // Fall back to plain text
        editorRef.current.innerText = text;
      }
      setReceiptText(text);
    }
  };

  const handleInput = () => {
    // Update text state when user types or edits
    if (editorRef.current) {
      setReceiptText(editorRef.current.innerText);
      setReceiptHtml(editorRef.current.innerHTML);
    }
  };

  const handleParse = () => {
    // Extract links from HTML if available
    const productLinks = receiptHtml ? extractProductData(receiptHtml) : {};
    const products = parseReceipt(receiptText, productLinks);
    setParsedProducts(products);
    setSelectedProducts(new Set(products.map((_, i) => i)));
    setStep("review");
  };

  const toggleProduct = (index) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedProducts(newSelected);
  };

  const handleSave = async () => {
    setSaving(true);
    setStep("saving");

    const productsToSave = parsedProducts.filter((_, i) =>
      selectedProducts.has(i),
    );

    let saved = 0;
    const failures = [];

    for (const product of productsToSave) {
      try {
        const entityData = {
          name: product.name,
          entity_type: "product",
          description: `Added from ${product.source}`,
        };

        // Set URL field
        if (product.url) {
          entityData.url = product.url;
        }

        // Use ASIN as SKU
        if (product.asin) {
          entityData.sku = product.asin;
        }

        if (
          product.purchasePrice !== null &&
          product.purchasePrice !== undefined
        ) {
          entityData.purchase_price = product.purchasePrice;
        }
        if (product.retailPrice !== null && product.retailPrice !== undefined) {
          entityData.retail_price = product.retailPrice;
          entityData.list_sale_price = product.retailPrice;
        }
        if (product.quantity && product.quantity > 1) {
          entityData.quantity = product.quantity;
        }
        if (product.purchaseDate) {
          // Format date as YYYY-MM-DD for the database
          entityData.purchase_date = product.purchaseDate
            .toISOString()
            .split("T")[0];
        }

        const { data: createdEntity } = await entities.create(entityData);

        // Download and save image if available
        if (product.imageUrl) {
          try {
            await media.downloadFromUrl(product.imageUrl, createdEntity.id);
          } catch (imgErr) {
            console.error("Failed to download image:", imgErr);
            // Continue even if image download fails
          }
        }

        saved++;
        setSavedCount(saved);
      } catch (err) {
        console.error("Failed to save product:", err);
        failures.push(product.name);
      }
    }

    setFailedProducts(failures);
    setStep("done");
    setSaving(false);
    refreshCounts();
    refreshProducts();
  };

  const handleDone = () => {
    onClose();
    navigate("/products");
  };

  // Handle keyboard shortcuts (Escape to close, Ctrl+Enter to accept)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        // Don't lose the progress view while the save loop is running
        if (step !== "saving") onClose();
      } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (step === "paste" && receiptText.trim()) {
          handleParse();
        } else if (step === "review" && selectedProducts.size > 0) {
          handleSave();
        } else if (step === "done") {
          handleDone();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, receiptText, selectedProducts.size]);

  if (!isOpen) return null;

  return (
    <div
      className="quick-add-overlay"
      onClick={step === "saving" ? undefined : onClose}
    >
      <div className="quick-add-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quick-add-header">
          <div className="quick-add-header-content">
            <DocumentIcon size={20} />
            <h2>Quick Add from Receipt</h2>
          </div>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            disabled={step === "saving"}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {step === "paste" && (
          <div className="quick-add-body">
            <p className="quick-add-description">
              Paste your receipt text below. Works with Amazon orders and most
              receipt formats.
            </p>
            <div
              ref={editorRef}
              className="quick-add-textarea quick-add-editor"
              contentEditable
              onPaste={handlePaste}
              onInput={handleInput}
              data-placeholder="Paste receipt text here..."
            />
            <div className="quick-add-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleParse}
                disabled={!receiptText.trim()}
              >
                Parse Receipt
              </button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="quick-add-body">
            {parsedProducts.length === 0 ? (
              <div className="quick-add-empty">
                <ProductIcon size={48} />
                <h3>No products found</h3>
                <p>
                  We couldn't detect any products in the pasted text. Try a
                  different format.
                </p>
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep("paste")}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                <p className="quick-add-description">
                  Found {parsedProducts.length} product
                  {parsedProducts.length !== 1 ? "s" : ""}. Select which ones to
                  add:
                </p>
                <div className="quick-add-product-list">
                  {parsedProducts.map((product, index) => (
                    <button
                      key={index}
                      className={`quick-add-product-item ${selectedProducts.has(index) ? "selected" : ""}`}
                      onClick={() => toggleProduct(index)}
                    >
                      <div
                        className={`quick-add-checkbox ${selectedProducts.has(index) ? "checked" : ""}`}
                      >
                        {selectedProducts.has(index) && <CheckIcon size={14} />}
                      </div>
                      {product.imageUrl && (
                        <img
                          src={product.imageUrl}
                          alt=""
                          className="quick-add-product-image"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <div className="quick-add-product-info">
                        <div className="quick-add-product-name">
                          {product.name}
                          {product.url && (
                            <span
                              className="quick-add-link-badge"
                              title="Product link captured"
                            >
                              <LinkIcon size={12} />
                            </span>
                          )}
                        </div>
                        <div className="quick-add-product-meta">
                          {product.purchasePrice !== null &&
                            product.purchasePrice !== undefined && (
                              <span className="quick-add-product-price">
                                {product.purchasePrice === 0 ? (
                                  <span style={{ color: "var(--success)" }}>
                                    Free
                                  </span>
                                ) : (
                                  `$${product.purchasePrice.toFixed(2)}`
                                )}
                                {product.retailPrice !== null &&
                                  product.retailPrice !==
                                    product.purchasePrice && (
                                    <span className="quick-add-original-price">
                                      {" "}
                                      (was ${product.retailPrice.toFixed(2)})
                                    </span>
                                  )}
                              </span>
                            )}
                          {product.quantity > 1 && (
                            <span className="quick-add-product-qty">
                              Qty: {product.quantity}
                            </span>
                          )}
                          {product.asin && (
                            <span className="quick-add-product-sku">
                              SKU: {product.asin}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="quick-add-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setStep("paste")}
                  >
                    Back
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={selectedProducts.size === 0}
                  >
                    Add {selectedProducts.size} Product
                    {selectedProducts.size !== 1 ? "s" : ""}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step === "saving" && (
          <div className="quick-add-body">
            <div className="quick-add-saving">
              <div className="spinner"></div>
              <p>
                Adding products... ({savedCount}/{selectedProducts.size})
              </p>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="quick-add-body">
            <div className="quick-add-done">
              <div className="quick-add-done-icon">
                <CheckIcon size={32} />
              </div>
              <h3>
                {failedProducts.length > 0
                  ? "Some Products Failed"
                  : "Products Added!"}
              </h3>
              <p>
                Successfully added {savedCount} product
                {savedCount !== 1 ? "s" : ""} to your inventory.
              </p>
              {failedProducts.length > 0 && (
                <div
                  style={{
                    marginTop: "var(--spacing-sm)",
                    padding: "var(--spacing-sm) var(--spacing-md)",
                    background: "var(--danger-bg, rgba(239, 68, 68, 0.1))",
                    color: "var(--danger, #ef4444)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "0.875rem",
                    textAlign: "left",
                  }}
                >
                  <strong>
                    {failedProducts.length} product
                    {failedProducts.length !== 1 ? "s" : ""} failed to save —
                    try adding {failedProducts.length !== 1 ? "them" : "it"}{" "}
                    again:
                  </strong>
                  <ul
                    style={{
                      margin: "var(--spacing-xs) 0 0",
                      paddingLeft: "1.25rem",
                    }}
                  >
                    {failedProducts.map((name, i) => (
                      <li key={i}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="quick-add-footer">
                <button className="btn btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleDone}>
                  View Products
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuickAddModal;
